// Frontend view types — mirror the backend public serialisers (bands only, no scores).
import type { BandKey } from './theme/bands';

export interface BandView {
  band: BandKey;
  label: string;
}

export type CriterionKey =
  | 'prompt_mastery'
  | 'technical_understanding'
  | 'practical_application'
  | 'critical_evaluation'
  | 'workflow_design';

export const CRITERION_LABELS: Record<CriterionKey, string> = {
  prompt_mastery: 'Prompt Mastery',
  technical_understanding: 'Technical Understanding',
  practical_application: 'Practical Application',
  critical_evaluation: 'Critical Evaluation',
  workflow_design: 'Workflow Design',
};

export type FourDKey = 'description' | 'delegation' | 'discernment' | 'diligence';
export type FourDStatus = 'strong' | 'in_place' | 'emerging';
export type Severity = 'high' | 'medium' | 'low';

export interface CriterionView {
  criterion: CriterionKey;
  band: BandView;
  strength: string;
  nextStep: string;
  evidenceQuotes: string[];
  confidence: 'low' | 'medium' | 'high';
}

/**
 * An organisation-authored supplementary dimension, scored alongside the universal model.
 * `label` is authoritative (org-defined) — do NOT look it up in a static map like CRITERION_LABELS.
 */
export interface CustomDimensionView {
  key: string; // org:<key>
  label: string;
  band: BandView;
  strength: string;
  nextStep: string;
  evidenceQuotes: string[];
  confidence: 'low' | 'medium' | 'high';
  informationalOnly?: boolean;
}

export interface ReportView {
  assessmentId: string;
  status: string;
  overallReadiness: BandView;
  selfRatedBand?: BandView;
  confidence: 'low' | 'medium' | 'high';
  velocity: 'low' | 'medium' | 'high';
  policyFit: 'compliant' | 'at_risk' | 'non_compliant';
  /** progression since the recipient's last completed assessment; absent for first-timers. */
  progression?: {
    priorBand: BandView;
    currentBand: BandView;
    direction: 'up' | 'flat' | 'down';
    bandLevelDelta: number;
    weeksElapsed: number;
    priorImported?: boolean;
  };
  criteria: CriterionView[];
  fourD: { dimension: FourDKey; status: FourDStatus }[];
  /** org-authored supplementary dimensions, rendered in their own section; absent when none. */
  customDimensions?: CustomDimensionView[];
  primaryBottleneck?: Bottleneck;
  secondaryBottleneck?: Bottleneck;
  lanePrescription?: string;
  policyViolations: { finding: string; severity: Severity; recommendedAction: string }[];
  policyViolationsCount: number;
  workflowFindings: { name: string; observation: string; opportunity: string }[];
  leverageSnapshot?: {
    hoursRecoverable: number;
    workflowsToTemplatise: number;
    targetQuarter: string;
    targetBand: BandView;
  };
  roadmap: { window: string; title: string; body: string }[];
  recommendedCertPath?: {
    name: string;
    focus: string;
    cert: { name: string; vendor: string; url: string };
    referenceUrl?: string;
  };
  immediateAction?: { title: string; body: string };
  practiceDrills: { title: string; rubricLine: string }[];
  practiceMasterPrompt?: string;
  durationMins?: number;
  completedAt?: string;
  // historical Notion import — top-line outcomes only; report renders a summary view.
  imported?: boolean;
  // display-only identity (not from the band-only payload; comes from profile/sample)
  name?: string;
  roleLine?: string;
}

// Per-criterion cohort average bands for the report's "compare to cohort" overlay.
export type CohortRadar = Partial<Record<CriterionKey, BandKey>>;

// Prompt templates — the Role/Context/Task/Output (RCTO) structure as reusable prompts.
export interface RctoFields {
  role: string;
  context: string;
  task: string;
  outputFormat: string;
}
export interface TemplateView {
  templateId: string;
  roleSlug: string;
  roleLabel: string;
  title: string;
  summary?: string;
  fields: RctoFields;
  prompt: string;
  source: 'generated' | 'fallback';
  generatedAt: string;
}
export interface UserTemplateView {
  templateId: string;
  title: string;
  fields: RctoFields;
  prompt: string;
  fromTemplateId?: string;
  createdAt: string;
}
export interface TemplatesResponse {
  roleLabel: string;
  library: TemplateView[];
  userTemplates: UserTemplateView[];
  approvedTools: string[];
}

export interface Bottleneck {
  key: string;
  label: string;
  whatsOverProtected: string;
  recommendedLane: string;
  reframe: string;
}

// Mirrors the backend Campaign entity (no server-only fields). Returned by getCampaign.
// One campaign == one organisation in the admin "Organisations" area.
export interface CampaignMeta {
  campaignId: string;
  name: string;
  sector?: string;
  internal?: boolean;
  periodStart?: string;
  periodEnd?: string;
  teamCount?: number;
  participantCount?: number;
  status: 'active' | 'closed' | 'draft' | 'archived';
  budgetUsd?: number;
  /** org-authored custom dimensions, for the admin editor to seed from. */
  customDimensions?: OrgDimensionSetView;
  // per-campaign settings (admin-editable)
  kAnonymityThreshold?: number;
  identifiedRosterPolicy?: 'allowed' | 'denied';
  modeOverride?: AssessmentMode;
}

// ---- Platform settings (admin Settings surface; mirrors backend PlatformConfig) ----
export interface VersionedBlockView { version: number; updatedAt: string; updatedBy?: string }

export interface ExperienceSettingsView extends VersionedBlockView {
  defaultMode: AssessmentMode;
  voiceEnabled: boolean;
  wsStreamingEnabled: boolean;
  quickModeTimeCopy: string;
  conversationalTimeCopy: string;
  spendThresholds?: { softUsd?: number; hardUsd?: number };
}

export interface AssessmentStructureSettingsView extends VersionedBlockView {
  stageRoundCounts?: { stage1: number; stage2: number; stage3: number };
  questionsPerTurn: { min: number; max: number };
  skips: { perStage: number; total: number };
  bands: { key: string; minScore: number; maxScore: number }[];
  criteria: { key: string; weight: number }[];
  fourDGateThreshold: number;
  stage1PassMinScore: number;
}

export interface BrandingSettingsView extends VersionedBlockView {
  productName: string;
  logoUrl?: string;
  colours: { primary: string; accent: string; code: string };
  consentCopy?: string;
  noticeCopy?: string;
}

export interface PlatformSettingsView {
  experience: ExperienceSettingsView;
  assessmentStructure: AssessmentStructureSettingsView;
  branding: BrandingSettingsView;
}

/** One row in the admin audit trail (GET /admin/audit). */
export interface AuditEventView {
  tsMs: number;
  eventType: string;
  actor: string;
  target?: string;
  detail?: string;
}

/** Participant-facing allow-listed settings (GET /platform/settings). */
export interface PublicSettingsView {
  defaultMode: AssessmentMode;
  voiceEnabled: boolean;
  wsStreamingEnabled: boolean;
  quickModeTimeCopy: string;
  conversationalTimeCopy: string;
  brandingVersion: number;
  branding: { productName: string; logoUrl?: string; colours: { primary: string; accent: string; code: string }; consentCopy?: string; noticeCopy?: string };
}

// ---- Org-custom dimension authoring (admin editor → POST /campaigns/{id}/dimensions) ----
// Mirrors the backend OrgDimensionSet contract: weight is a 0..1 within-group weight (the scorer
// re-normalises), blend defaults to sidecar.
export type OrgBlend = { mode: 'sidecar' } | { mode: 'blended'; groupWeight: number };

export interface CustomDimensionDefView {
  /** omitted for a new row — the server derives an org:<slug> key from the label. */
  key?: string;
  label: string;
  rubric: string;
  weight: number;
  informationalOnly?: boolean;
  retired?: boolean;
}

export interface OrgDimensionSetView {
  version: number;
  dimensions: CustomDimensionDefView[];
  blend: OrgBlend;
  updatedAt?: string;
  updatedBy?: string;
}

// One row of the Organisations index (GET /campaigns). Campaign meta plus the per-org
// summary the list screen renders without a follow-up call. Amounts are USD (display
// converts to AUD); budgetUsd is null when no spend cap is set.
export interface OrgSummary {
  campaignId: string;
  name: string;
  sector?: string;
  internal?: boolean;
  status: 'active' | 'closed' | 'draft' | 'archived';
  periodStart?: string;
  periodEnd?: string;
  participantCount: number;
  completeCount: number;
  completionPct: number;
  avgReadiness: BandView;
  spentUsd: number;
  budgetUsd: number | null;
}

export interface AdminAnalytics {
  campaignId: string;
  kAnonymity: number;
  kpis: {
    completionPct: number;
    completeCount: number;
    inProgressCount: number;
    notStartedCount: number;
    participantCount: number;
    avgReadiness: BandView;
  };
  capabilityRadar: { criterion: CriterionKey; band: BandView }[];
  fourDDistribution: { dimension: FourDKey; strong: number; in_place: number; emerging: number }[];
  topBottlenecks: { key: string; label: string; pct: number }[];
  readinessByTeam: { team: string; band: BandView; participantCount: number; suppressed: boolean }[];
  policyRisk: { compliant: number; at_risk: number; non_compliant: number };
  // name/email only present in the identified (admin-only, audit-logged) roster
  roster: { id: string; role: string; team: string; status: string; band?: BandView; name?: string; email?: string }[];
  suppressedTeamCount: number;
  hiddenParticipantCount: number;
  /** org-custom dimension cohort for the current set version; absent when none or below k-anonymity. */
  customDimensionCohort?: {
    version: number;
    cohortSize: number;
    dimensions: { key: string; label: string; band: BandView; informationalOnly?: boolean }[];
  };
}

// Campaign spend view (Budget screen). Mirrors the backend BudgetResponse: amounts are
// USD (the screen converts to AUD for display). costUsd/headroomUsd are null when
// not applicable (not started / no budget set).
export interface BudgetCostRow {
  id: string;
  role: string;
  team: string;
  durationMins: number | null;
  tokens: number | null; // raw token count; UI shows thousands
  costUsd: number | null;
}
export interface BudgetDto {
  campaignId: string;
  campaignName?: string;
  currency: 'USD';
  spentUsd: number;
  projectedRemainingUsd: number;
  headroomUsd: number | null;
  budgetUsd: number | null;
  breakdown: { key: string; label: string; pct: number; amountUsd: number }[];
  rows: BudgetCostRow[];
  rowsShown: number;
  rowsStarted: number;
  sampleSubtotalUsd: number;
}

// Mirrors the backend PracticeScore (practice-coach.ts).
export interface PracticeRubricItem {
  label: string;
  met: boolean;
  note: string;
}
export interface PracticeScore {
  score: number;
  rubric: PracticeRubricItem[];
  improved: string;
  whatChanged: string;
}

// Mirrors the backend PracticeCompletion (handlers/practice.ts).
export type PracticeItemType = 'drill' | 'action';
export interface PracticeCompletion {
  itemType: PracticeItemType;
  itemRef: string;
  assessmentId?: string;
  done: boolean;
  reflection?: string;
  completedAt: string;
}

// UI/session-level assessment mode (mirrors backend AssessmentMode): the quick 6-step
// walkthrough vs the conversational Avery interview. Distinct from the internal full|lite track.
export type AssessmentMode = 'quick' | 'conversational';

// Mirrors the backend PublicSession / PublicMessage serialisers (session.ts).
export interface SessionSummary {
  assessmentId: string;
  status: 'created' | 'in_progress' | 'completed' | 'abandoned' | 'expired' | 'archived';
  stage: number;
  mode: AssessmentMode;
  startedAt?: string;
  lastActivityAt?: string;
}

export interface TranscriptMessage {
  ts: string;
  role: 'avery' | 'user';
  type: 'text' | 'question' | 'assumption' | 'reflection';
  text: string;
}

export interface ChatMessage {
  id: string;
  role: 'avery' | 'user';
  type: 'text' | 'question' | 'assumption' | 'reflection';
  text: string;
  // assumption card resolution
  resolved?: 'confirmed' | 'corrected';
  // reflection extra body
  insight?: string;
}
